import Big from "big.js";
import { zipObj } from "ramda";
import { ReduxActions } from "./actions";
import { initialState, PortfoliosType, ReduxStateType } from "./index";
import { calculateAssetsTotalAmount, generatePortfolioName } from "../utils";

export const reducer = (
  state = initialState,
  action: ReduxActions
): ReduxStateType => {
  switch (action.type) {
    case "SET_STORE_FROM_SHARED_LINK":
      return {
        ...state,
        ...action.payload,
      };
    case "SHOW_SPINNER":
      return {
        ...state,
        spinner: true,
      };
    case "SET_LANGUAGE":
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          language: action.payload,
        },
      };
    case "SET_CURRENCY":
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          currency: {
            ...state.globalConfigs.currency,
            currentCurrency: action.payload,
          },
        },
      };
    case "SET_PORTFOLIO_SELECTED_ID":
      return {
        ...state,
        currentPortfolioId: action.payload,
      };
    case "ADD_OR_UPDATE_ASSET_TO_PORTFOLIO": {
      const newState = { ...state };
      switch (action.rule) {
        case "RULE_ADD_ASSET_INVESTMENT_VALUE": {
          newState.portfolios[action.portfolioId].assets = {
            ...state.portfolios[action.portfolioId].assets,
            [`${String(action.payload.assetType)}❤❤❤${String(
              action.payload.identifier
            )}`]: {
              asset: action.payload,
              percentage: null,
              amount: action.amount,
            },
          };

          const totalInvestmentValue = calculateAssetsTotalAmount(
            newState.portfolios[action.portfolioId].assets
          );
          newState.portfolios[action.portfolioId].totalAmount =
            totalInvestmentValue;

          if (totalInvestmentValue === 0) {
            Object.keys(newState.portfolios[action.portfolioId].assets).forEach(
              (assetKey) => {
                newState.portfolios[action.portfolioId].assets[
                  assetKey
                ].percentage = 0;
              }
            );
          } else {
            Object.keys(newState.portfolios[action.portfolioId].assets).forEach(
              (assetKey) => {
                newState.portfolios[action.portfolioId].assets[
                  assetKey
                ].percentage = Big(
                  newState.portfolios[action.portfolioId].assets[assetKey]
                    .amount || 0
                )
                  .div(totalInvestmentValue)
                  .times(100)
                  .toNumber();
              }
            );
          }
          break;
        }
        case "RULE_EDIT_ASSET_PERCENTAGE": {
          newState.portfolios[action.portfolioId].assets = {
            ...state.portfolios[action.portfolioId].assets,
            [`${String(action.payload.assetType)}❤❤❤${String(
              action.payload.identifier
            )}`]: {
              asset: action.payload,
              percentage: action.percentage || 0,
              amount:
                action.percentage === null
                  ? 0
                  : Big(action.percentage)
                      .times(state.portfolios[action.portfolioId].totalAmount)
                      .div(100)
                      .toNumber(),
            },
          };
          break;
        }
        default:
      }
      return newState;
    }
    case "REMOVE_ASSET_FROM_PORTFOLIO": {
      const newAssets = { ...state.portfolios[action.portfolioId].assets };
      delete newAssets[action.payload];
      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [action.portfolioId]: {
            ...state.portfolios[action.portfolioId],
            assets: newAssets,
          },
        },
      };
    }
    case "RENAME_PORTFOLIO":
      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [action.portfolioId]: {
            ...state.portfolios[action.portfolioId],
            name: action.payload,
          },
        },
      };
    case "UPDATE_PORTFOLIO_TOTAL": {
      const newState = { ...state };
      Object.keys(newState.portfolios[action.portfolioId].assets).forEach(
        (assetKey) => {
          newState.portfolios[action.portfolioId].assets[assetKey].amount = Big(
            newState.portfolios[action.portfolioId].assets[assetKey]
              .percentage || 0
          )
            .div(100)
            .times(action.payload)
            .toNumber();
        }
      );
      newState.portfolios[action.portfolioId].totalAmount = action.payload;
      return newState;
    }
    case "SET_PERIOD":
      return {
        ...state,
        period: action.payload,
      };
    case "ADD_PORTFOLIO":
      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [Object.keys(state.portfolios).length]: {
            name: generatePortfolioName({ ...state.portfolios }),
            totalAmount: 0,
            identifier: "",
            assetType: "PORTFOLIO",
            assets: {},
          },
        },
      };
    case "CLONE_PORTFOLIO": {
      const clonedPortfolio = {
        ...state.portfolios[action.cloneFromId],
        identifier: action.remoteId,
        name: `Clone - ${state.portfolios[action.cloneFromId].name}`,
      };

      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [Object.keys(state.portfolios).length]: { ...clonedPortfolio },
        },
      };
    }
    case "ADD_BENCHMARK":
      return {
        ...state,
        benchmarks: {
          ...state.benchmarks,
          [`${String(action.payload.assetType)}❤❤❤${String(
            action.payload.identifier
          )}`]: {
            asset: { ...action.payload },
          },
        },
      };
    case "REMOVE_BENCHMARK": {
      const newBenchmarks = { ...state.benchmarks };
      delete newBenchmarks[action.payload];

      return {
        ...state,
        benchmarks: {
          ...newBenchmarks,
        },
      };
    }
    case "BALANCE_PORTFOLIO": {
      const newAssets = { ...state.portfolios[action.portfolioId].assets };
      const assetPercentage =
        Object.keys(newAssets).length === 0
          ? 0
          : Big(100).div(Object.keys(newAssets).length).toNumber();
      Object.keys(newAssets).forEach((assetKey) => {
        newAssets[assetKey].percentage = assetPercentage;
        newAssets[assetKey].amount = Big(assetPercentage)
          .times(state.portfolios[action.portfolioId].totalAmount)
          .div(100)
          .toNumber();
      });
      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [action.portfolioId]: {
            ...state.portfolios[action.portfolioId],
            assets: newAssets,
          },
        },
      };
    }
    case "ADD_OR_UPDATE_PORTFOLIO_REMOTE_ID": {
      const remoteId =
        action.remotePortfolioId === "failed"
          ? state.portfolios[action.portfolioId].identifier
          : action.remotePortfolioId;

      return {
        ...state,
        portfolios: {
          ...state.portfolios,
          [action.portfolioId]: {
            ...state.portfolios[action.portfolioId],
            identifier: remoteId,
          },
        },
      };
    }
    case "SET_APPLICATION_AMOUNT": {
      return {
        ...state,
        applicationAmount: action.payload,
      };
    }
    case "SET_SHOW_SIDE_EDITOR": {
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          showSideEditor: action.payload,
        },
      };
    }
    case "UPDATE_PORTFOLIO_BEING_EDITED": {
      const openSidePanel = action.payload !== null;
      return {
        ...state,
        currentPortfolioId:
          action.payload === null ? state.currentPortfolioId : action.payload,
        globalConfigs: {
          ...state.globalConfigs,
          portfolioBeingEdited: action.payload,
          showSideEditor: openSidePanel,
        },
      };
    }
    case "SET_SHOW_ASSETS": {
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          showAssets: action.payload,
        },
      };
    }
    case "REMOVE_PORTFOLIO": {
      const portfoliosArrayed = Object.keys(state.portfolios).map(
        (portfolioKey) => state.portfolios[Number(portfolioKey)]
      );
      portfoliosArrayed.splice(action.payload, 1);

      const isPortfoliosEmpty = portfoliosArrayed.length === 0;
      const initialPortfolio: PortfoliosType = {
        0: {
          name: generatePortfolioName({}),
          totalAmount: 0,
          identifier: "",
          assetType: "PORTFOLIO",
          assets: {},
        },
      };
      const updatedPortfolios = zipObj(
        portfoliosArrayed.map((_portfolio, index) => String(index)),
        portfoliosArrayed
      );

      return {
        ...state,
        currentPortfolioId: 0,
        portfolios: isPortfoliosEmpty
          ? { ...initialPortfolio }
          : {
              ...updatedPortfolios,
            },
      };
    }
    case "SET_CUSTOM_DATE_RANGE": {
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          customDateRange: action.payload,
        },
      };
    }
    case "SET_FETCHED_DATE_RANGE": {
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          fetchedDateRange: action.payload,
        },
      };
    }
    case "SET_WINDOW": {
      return {
        ...state,
        windowValue: action.payload,
      };
    }
    case "SET_CONSOLIDATED": {
      return {
        ...state,
        consolidated: action.payload,
      };
    }
    case "SET_COMPETENCE": {
      return {
        ...state,
        competence: action.payload,
      };
    }
    case "SET_SHARE_ERROR":
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          showShareError: action.payload,
        },
      };
    case "SET_LOADING_FROM_LINK":
      return {
        ...state,
        sharedLinkTasks: {
          ...state.sharedLinkTasks,
          [action.task]: action.payload,
        },
      };
    case "SET_AVAILABLE_COMPETENCES":
      return {
        ...state,
        globalConfigs: {
          ...state.globalConfigs,
          availableCompetences: action.payload,
        },
      };
    default:
      return state;
  }
};
